import { T, useTranslate } from '@tolgee/react'
import axios from 'axios'
import classNames from 'classnames'
import { ContextModal } from 'components/document-context/ContextModal'
import { HelpTooltips } from 'components/help/helpTooltips'
import { useDocumentContext } from 'components/hooks/context'
import { Loader, ViewContext } from 'components/lib'
import { errorToast, successToast } from 'helpers'
import { ToolLayoutPrimary, ChatbotGeneratorHeader, ChatActions } from 'ui'
import {
  CHATBOT_AVAILABILITY_OPTIONS,
  compareChatbotConfigs,
  handleTelegramWebHook,
  ignoredFieldPerType,
  syncContextFromModel,
} from 'helpers/chatbot'
import {
  docTypes,
  imageTypes,
  PUBLISH_STATE_ACTIVE,
  PUBLISH_STATE_PENDING,
  PUBLISH_STATE_UNPUBLISHED,
} from 'helpers/enums'
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import {
  chatBotInitialState,
  generateChatBot,
  generateChatBotDraft,
  getChatBotList,
  SelectChatBotInputValues,
  SelectChatBotListData,
  SelectChatBotSettings,
  SelectLoadChatsAsyncStates,
  setChatBotInputValues,
  setClearFields,
  setSelectedChatBotSettings,
  setSelectedChatBotValues,
} from 'store/account/chatBot'
import { DEFAULT_MODAL_DATA, Modal } from 'toolComponents/generic/modal'
import { v4 } from 'uuid'
import ChatbotPreviewWrapper from './chatbotPreviewWrapper'
import { ChatbotPublishing } from './chatBotPublishing'
import ChatGenerator from './chatGenerator'
import DuplicateModalView from 'toolComponents/chat-bot/library/DuplicateModalView'
import { Input, Microbutton, ReactSelect } from 'ui'
import { useGalleries } from 'components/hooks/galleries'
import { CircularProgress } from '@mui/material'
import { CheckCircle } from '@mui/icons-material'

// Tour steps
// const steps = [
//   {
//     target: '#selector',
//     content: (
//       <T keyName='eleo-tour-chatbot-1'>
//         Update your Chatbot. If you have previously created a chatbot, you can choose it here for
//         editing.
//       </T>
//     ),
//   },
//   {
//     target: '#name',
//     content: (
//       <T keyName='eleo-tour-chatbot-2'>
//         Choose a name for your Chatbot. This name will also appear in the chatbot link.
//       </T>
//     ),
//   },
//   {
//     target: '#story',
//     content: (
//       <T keyName='eleo-tour-chatbot-3'>
//         Add Your Story. Your Chatbot will draw information about you, your company, or how to behave
//         from the Your Story indicated here.
//       </T>
//     ),
//   },
//   {
//     target: '#advanced',
//     content: (
//       <T keyName='eleo-tour-chatbot-4'>
//         Select additional options. Set the tone, language, introduction, avatar, and Chatbot
//         response length.
//       </T>
//     ),
//   },
//   {
//     target: '#submit',
//     content: (
//       <T keyName='eleo-tour-chatbot-5'>
//         Create or update the Chatbot. You can have multiple different Chatbots running.
//       </T>
//     ),
//   },
//   {
//     target: '#output',
//     content: (
//       <T keyName='eleo-tour-chatbot-6'>
//         Manage your Chatbot. Copy the chatbot link, place the Chatbot on your website or on
//         Telegram.
//       </T>
//     ),
//   },
// ]

const mapChatbotParams = (data, setId = true) => {
  return {
    ...(setId && { selectedChatBot: data.id }),
    avatar: data.avatar,
    language: data.language,
    intro: data.intro,
    monthlyLimit: data.monthlyLimit,
    isMonthlyLimit: data.isMonthlyLimit ?? true,
    name: data.name,
    responseLength: data.responseLength,
    storyId: data.storyId,
    tone: data.tone,
    wordsConsumed: data.wordsConsumed,
    category: data.category,
    voice: data.voice,
    isPublic: data.isPublic,
    modelId: data.modelId,
    displayName: data.displayName,
    shortDescription: data.shortDescription,
    longDescription: data.longDescription,
    allowWebSearch: data.allowWebSearch,
    allowContext: data.allowContext,
    allowDatabases: data.allowDatabases,
    relatedGroups: data?.relatedGroups,
    visibilityOption: data?.visibilityOption,
    availabilityOption: data?.availabilityOption ?? CHATBOT_AVAILABILITY_OPTIONS[0].value,
    author: data.author,
    allowLanguageChange: data.allowLanguageChange,
    allowModeChange: data.allowModeChange,
    defaultMode: data.defaultMode ?? 'text',
    isStartWithAudio: data.isStartWithAudio,
    isStartWithWebSearch: data.isStartWithWebSearch,
    instructions: data.instructions ?? '',
    customUrlName: data.customUrlName ?? null,
    enableCustomUrl: data.enableCustomUrl ?? false,
    customUrls: data.customUrls ?? {
      forum: { enableCustomUrl: false, customUrlName: null },
      public: { enableCustomUrl: false, customUrlName: null },
    },
    forum: data.forum ?? {
      displayName: '',
      shortDescription: '',
      longDescription: '',
      category: 'eleo-chatbot-category-other',
    },
    documents: data.documents,
    stories: data.stories,
    maxConversationLength: data.maxConversationLength ?? {
      forum: 10000,
      public: 10000,
      embed: 10000,
    },
    databases: data.databases ?? [],
  }
}

const ChatBot = forwardRef(({ handleHideModal, chatbotId, onCreateCallback }, ref) => {
  const { t } = useTranslate()
  const params = useSearchParams()[0]
  const dispatch = useDispatch()
  const documentContext = useDocumentContext('chatbot-creator')
  const context = useContext(ViewContext)

  const chatBotInputValues = useSelector(SelectChatBotInputValues)
  const chatBotList = useSelector(SelectChatBotListData)
  const chatBotSettings = useSelector(SelectChatBotSettings)
  const isFetchChatbotlistLoading = useSelector(SelectLoadChatsAsyncStates).loading

  const [folded, setFolded] = useState(false)
  const [uplodLoading, setUploadLoading] = useState(false)
  const [isShowAdvanced, setIsShowAdvanced] = useState(false)
  const [isTourOverride, setIsTourOverride] = useState(false)
  const [isShowPublishing, setIsShowPublishing] = useState(true)
  const [chatbotServerState, setChatbotServerState] = useState()
  const [modalData, setModalData] = useState(DEFAULT_MODAL_DATA)
  const [chatbotDraftServerState, setChatbotDraftServerState] = useState()
  const [publishState, setPublishState] = useState(
    chatbotId ? PUBLISH_STATE_PENDING : PUBLISH_STATE_UNPUBLISHED
  )
  const [loadingState, setLoadingState] = useState({
    publish: false,
    draft: false,
    chatbot: Boolean(chatbotId),
  })

  const {
    galleries,
    galleryItems,
    gallerySearch,
    setGallerySearch,
    selectedGallery,
    setGalleryItems,
    setSelectedGallery,
  } = useGalleries()

  const isDocumentContextChanged =
    (chatbotServerState &&
      (documentContext.docContext.length !==
        chatbotServerState.inputValues.documents?.length +
          chatbotServerState.inputValues.stories?.length ||
        !documentContext.docContext.every(
          (i1) =>
            chatbotServerState.inputValues.documents?.some((i2) => i2.name === i1.label) ||
            chatbotServerState.inputValues.stories?.some((i2) => i2.name === i1.label)
        ))) ||
    (chatbotDraftServerState &&
      (documentContext.docContext.length !==
        chatbotDraftServerState.inputValues.documents?.length +
          chatbotDraftServerState.inputValues.stories?.length ||
        !documentContext.docContext.every(
          (i1) =>
            chatbotDraftServerState.inputValues.documents?.some((i2) => i2.name === i1.label) ||
            chatbotDraftServerState.inputValues.stories?.some((i2) => i2.name === i1.label)
        )))

  const isDatabaseChanged =
    (chatbotServerState &&
      (documentContext.databases.length !== chatbotServerState.inputValues.databases?.length ||
        !documentContext.databases.every((i1) =>
          chatbotServerState.inputValues.databases?.some((i2) => i2.name === i1.name)
        ))) ||
    (chatbotDraftServerState &&
      (documentContext.databases.length !== chatbotDraftServerState.inputValues.databases?.length ||
        !documentContext.databases.every((i1) =>
          chatbotDraftServerState.inputValues.databases?.some((i2) => i2.name === i1.name)
        )))

  // Update is pending if either:
  // No data from server and the form is not in default state
  // There is data from server and its not matching the form
  // There is data from server and the publishing options dont match
  const isUpdatePending =
    (!chatbotServerState && chatBotInputValues !== chatBotInitialState.inputValues) ||
    isDocumentContextChanged ||
    isDatabaseChanged ||
    (chatbotServerState &&
      !compareChatbotConfigs(
        { inputValues: chatBotInputValues, settings: chatBotSettings },
        chatbotServerState,
        ignoredFieldPerType.global
      )[0]) ||
    (chatbotServerState &&
      Object.keys(chatBotSettings.publishing || {}).some(
        (type) => chatBotSettings.publishing[type] !== chatbotServerState.settings.publishing[type]
      ))

  const isDraftSaved = Boolean(
    !isDocumentContextChanged &&
      !isDatabaseChanged &&
      chatbotDraftServerState &&
      compareChatbotConfigs(
        { inputValues: chatBotInputValues, settings: chatBotSettings },
        chatbotDraftServerState,
        ignoredFieldPerType.global
      )[0] &&
      Object.keys(chatBotSettings.publishing || {}).every(
        (type) =>
          chatBotSettings.publishing[type] === chatbotDraftServerState.settings.publishing[type]
      )
  )

  // Compare current form values to config from server to update the publishing section badges
  useEffect(() => {
    if (chatbotServerState) {
      setPublishState((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([type, _]) => {
            if (
              !chatbotDraftServerState?.settings.publishing?.[type] &&
              !chatbotServerState?.settings.publishing?.[type]
            )
              return [type, 'unpublished']

            return compareChatbotConfigs(
              chatbotServerState,
              { inputValues: chatBotInputValues, settings: chatBotSettings },
              ignoredFieldPerType[type]
            )[0] &&
              !isDocumentContextChanged &&
              !isDatabaseChanged
              ? [type, 'active']
              : [type, 'pending']
          })
        )
      )
    }
  }, [
    chatBotInputValues,
    chatbotServerState,
    chatbotDraftServerState,
    setPublishState,
    chatBotSettings,
    isDocumentContextChanged,
    isDatabaseChanged,
  ])

  useEffect(() => {
    if (!params.get('publishing') || !chatBotList.length) {
      dispatch(getChatBotList())
      documentContext.clearContext()
    }

    return () => {
      dispatch(setClearFields())
    }
  }, [dispatch])

  const chatbotLoaded = useRef(false)
  useEffect(() => {
    if (!chatbotLoaded.current) {
      if (!chatbotId) {
        chatbotLoaded.current = true
        dispatch(setClearFields())
      } else if (chatBotList.length) {
        chatbotLoaded.current = true
        setTimeout(
          () => {
            setLoadingState((prev) => ({ ...prev, chatbot: false }))
          },
          params.get('publishing') ? 100 : 500
        )
        const requestedChatbot = chatBotList.find((bot) => bot.id === chatbotId)
        if (requestedChatbot) handleSelectBot(requestedChatbot.id)
      }
    }
  }, [chatBotList, chatbotId, dispatch])

  const handleGenerateChatBot = async (saveAsDraft = false, createDuplicate = false, newName) => {
    if (!chatBotInputValues.name?.length) {
      if (saveAsDraft)
        errorToast(
          <T keyName='eleo-chat-bot-name-required-draft'>Enter chatbot name before saving</T>
        )
      else
        errorToast(
          <T keyName='eleo-chat-bot-name-required'>Enter chatbot name before publishing</T>
        )
      return
    }
    if (
      !saveAsDraft &&
      !Object.values(chatBotSettings.publishing || {}).some((type) => type === true)
    ) {
      errorToast(<T keyName='eleo-chat-bot-publishing-type-required'>Select publication type</T>)
      setIsShowPublishing(true)
      return
    }

    if (saveAsDraft) setLoadingState((prev) => ({ ...prev, draft: true }))
    else setLoadingState((prev) => ({ ...prev, publish: true }))

    const newId = v4()

    // Enable telegram when:
    // publishing: telegram - is true
    // There is no server state or the server state has telegram turned off
    let telegramBotUsername
    if (
      !saveAsDraft &&
      chatBotSettings.telegramToken?.length &&
      chatBotSettings.publishing.telegram &&
      (!chatbotServerState?.settings?.publishing.telegram ||
        chatbotServerState?.settings.telegramToken !== chatBotSettings.telegramToken)
    ) {
      telegramBotUsername = await handleTelegramWebHook(
        chatBotSettings.telegramToken,
        true,
        'setWebhook',
        true,
        chatBotInputValues.selectedChatBot || newId
      )
    }
    // Disable telegram if there was a server state where it was turned on
    else if (
      !chatBotSettings.publishing.telegram &&
      chatbotServerState?.settings?.publishing.telegram
    ) {
      await handleTelegramWebHook(chatBotSettings.telegramToken, false, 'deleteWebhook', true)
    }

    if (!saveAsDraft) {
      dispatch(
        generateChatBot({
          ...chatBotInputValues,
          settings: {
            ...chatBotSettings,
            ...(telegramBotUsername && { telegramBotUsername: telegramBotUsername }),
          },
          stories: documentContext.docContext.filter((item) => item.type === 'story'),
          context: documentContext.docContext.filter((item) => item.type !== 'story'),
          databases: documentContext.databases.map((db) => ({ id: db.id, name: db.name })),
          newId: newId,
        })
      ).then((res) => {
        setLoadingState((prev) => ({ ...prev, publish: false }))

        if (res.type === 'chatBot/generate/fulfilled' && res.payload) {
          onCreateCallback?.()
          setPublishState(PUBLISH_STATE_ACTIVE)

          const { settings, ...inputValues } = res.payload

          dispatch(setSelectedChatBotValues(mapChatbotParams(inputValues, true)))

          setChatbotServerState({ inputValues, settings })
          setChatbotDraftServerState()
        }
      })
    } else {
      dispatch(
        generateChatBotDraft({
          ...chatbotDraftServerState?.inputValues,
          ...chatBotInputValues,
          settings: chatBotSettings,
          stories: documentContext.docContext.filter((item) => item.type === 'story'),
          context: documentContext.docContext.filter((item) => item.type !== 'story'),
          databases: documentContext.databases.map((db) => ({ id: db.id, name: db.name })),
          ...(newName && { name: newName }),
          ...(newName && { id: undefined }),
          selectedChatBot:
            createDuplicate || chatbotDraftServerState ? null : chatBotInputValues.selectedChatBot,
        })
      ).then((res) => {
        setLoadingState((prev) => ({ ...prev, draft: false }))

        if (res.type === 'chatBot/draft/generate/fulfilled' && res.payload) {
          onCreateCallback?.()

          const { settings, ...inputValues } = res.payload
          dispatch(setChatBotInputValues({ field: 'name', value: inputValues.name }))

          dispatch(setSelectedChatBotValues(mapChatbotParams(inputValues, false)))

          setChatbotDraftServerState({ inputValues, settings })
          setChatbotServerState()

          if (createDuplicate) {
            successToast(
              <T keyName='eleo-toast-chatbot-draft-duplicated'>
                Your chatbot has been duplicated as draft. To publish the new draft please change
                the name to be unique.
              </T>
            )
          } else {
            if (chatbotDraftServerState) {
              successToast(<T keyName='eleo-draft-saved'>Draft saved</T>)
            }
          }
        }
      })
    }
  }

  async function handleFileUpload(e, handler, dir) {
    try {
      setUploadLoading(true)

      const url = '/api/utility/upload'
      let data = new FormData()
      data.append('file', e.target.files[0])
      data.append('dir', dir ? dir : 'chatBot/')

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      }

      const res = await axios.post(url, data, config)
      handler(res.data.items[0])
    } catch (err) {
      console.log(err)
    } finally {
      setUploadLoading(false)
    }
  }

  function handleTourProgress(e) {
    if (e.action === 'reset' || e.action === 'close' || e.action === 'stop') {
      setIsTourOverride(false)
      return
    }
    if (!e.action === 'update') return

    const step = e.index
    if (step === 0) setIsTourOverride(true)
  }

  const handleSelectBot = (chatbotId) => {
    let selectedChatbot = chatBotList.find((bot) => bot.id === chatbotId)
    if (!selectedChatbot) return

    const values = mapChatbotParams(selectedChatbot, false)
    dispatch(setSelectedChatBotValues(values))

    dispatch(setSelectedChatBotSettings(selectedChatbot.settings))

    syncContextFromModel(documentContext, selectedChatbot)

    let botId
    if (selectedChatbot.type === 'draft' && selectedChatbot.originalChatBotId?.length) {
      botId = selectedChatbot.originalChatBotId
    } else botId = selectedChatbot.id

    dispatch(
      setChatBotInputValues({
        field: 'selectedChatBot',
        value: botId,
      })
    )

    if (selectedChatbot.type === 'draft')
      setChatbotDraftServerState({
        inputValues: { ...values, id: selectedChatbot.id },
        settings: selectedChatbot.settings,
      })
    else
      setChatbotServerState({
        inputValues: values,
        settings: selectedChatbot.settings,
      })
  }

  const handleOverrideBot = async (idToOverride, newChatbotId) => {
    if (!idToOverride) return
    const name = chatBotInputValues.name
    const id = chatBotInputValues.selectedChatBot

    try {
      let selectedChatbot = chatBotList.find((bot) => bot.id === newChatbotId)
      if (!selectedChatbot) return

      const values = {
        selectedChatBot: id,
        name: name,
        avatar: selectedChatbot.avatar,
        language: selectedChatbot.language,
        intro: selectedChatbot.intro,
        monthlyLimit: selectedChatbot.monthlyLimit,
        isMonthlyLimit: selectedChatbot.isMonthlyLimit ?? true,
        responseLength: selectedChatbot.responseLength,
        storyId: selectedChatbot.storyId,
        tone: selectedChatbot.tone,
        wordsConsumed: selectedChatbot.wordsConsumed,
        category: selectedChatbot.category,
        voice: selectedChatbot.voice,
        isPublic: selectedChatbot.isPublic,
        modelId: selectedChatbot.modelId,
        displayName: selectedChatbot.displayName,
        shortDescription: selectedChatbot.shortDescription,
        longDescription: selectedChatbot.longDescription,
        allowWebSearch: selectedChatbot.allowWebSearch,
        allowContext: selectedChatbot.allowContext,
        allowDatabases: selectedChatbot.allowDatabases,
        relatedGroups: selectedChatbot?.relatedGroups,
        visibilityOption: selectedChatbot?.visibilityOption,
        availabilityOption:
          selectedChatbot?.availabilityOption ?? CHATBOT_AVAILABILITY_OPTIONS[0].value,
        author: selectedChatbot.author,
        allowLanguageChange: selectedChatbot.allowLanguageChange,
        allowModeChange: selectedChatbot.allowModeChange,
        defaultMode: selectedChatbot.defaultMode ?? 'text',
        isStartWithAudio: selectedChatbot.isStartWithAudio,
        instructions: selectedChatbot.instructions ?? '',
        customUrls: selectedChatbot.customUrls ?? {
          forum: { enableCustomUrl: false, customUrlName: null },
          public: { enableCustomUrl: false, customUrlName: null },
        },
        forum: selectedChatbot.forum ?? {
          displayName: '',
          shortDescription: '',
          longDescription: '',
          category: 'eleo-chatbot-category-other',
        },
      }

      dispatch(setSelectedChatBotValues(values))

      dispatch(setSelectedChatBotSettings(selectedChatbot.settings))

      syncContextFromModel(documentContext, selectedChatbot)
    } catch (err) {
      context.handleError(err)
    }
  }

  const handleCloseModal = useCallback(() => {
    if (isUpdatePending && !isDraftSaved) {
      setModalData({
        isVisible: true,
        title: (
          <T
            keyName='eleo-chatbot-modal-unsaved-changes-title'
            defaultValue='You have unsaved changes'
          />
        ),
        subtitle: (
          <T
            keyName='eleo-chatbot-modal-unsaved-changes-subtitle'
            defaultValue='You will lose permamently all the changes you applied.'
          />
        ),
        acceptLabel: <T keyName='eleo-discard-and-close'>Discard and close</T>,
        cancelLabel: <T keyName='eleo-back'>Back</T>,
        type: 'unsaved-changes',
        context: {},
      })
    } else handleHideModal()
  }, [handleHideModal, isDraftSaved, isUpdatePending])

  const generatorLayout = (
    <ChatGenerator
      isShowAdvanced={isTourOverride || isShowAdvanced}
      setIsShowAdvanced={setIsShowAdvanced}
      isShowPublishing={isShowPublishing}
      setIsShowPublishing={setIsShowPublishing}
      handleGenerateChatBot={handleGenerateChatBot}
      handleFileUpload={handleFileUpload}
      documentContext={documentContext}
      publishState={publishState}
      setPublishState={setPublishState}
      chatbotServerState={chatbotServerState}
      chatbotDraftServerState={chatbotDraftServerState}
      isEditMode={Boolean(chatbotServerState || chatbotDraftServerState)}
      loadingState={loadingState}
      handleResetToServerState={() =>
        (chatbotServerState || chatbotDraftServerState) &&
        handleSelectBot(chatBotInputValues.selectedChatBot)
      }
      isUpdatePending={isUpdatePending}
      isDraftSaved={isDraftSaved}
      setModalData={setModalData}
      isDraft={Boolean(chatbotDraftServerState)}
    />
  )

  const previewLayout = (
    <ChatbotPreviewWrapper
      chatBotSettings={chatBotSettings}
      chatBotInputValues={chatBotInputValues}
      isDraft={Boolean(chatbotDraftServerState)}
      isReady={Boolean(chatbotDraftServerState || chatbotServerState)}
      chatbotContext={documentContext}
    />
  )

  const publishingLayout = (
    <div className='flex h-full flex-col items-center justify-between pt-2'>
      <div className='h-[calc(100%-73px)] w-full overflow-x-clip overflow-y-scroll'>
        <div className='flex justify-center'>
          <ChatbotPublishing
            isPanelLarge={true}
            isShowPublishing={true}
            publishState={publishState}
            setPublishState={setPublishState}
            chatbotServerState={chatbotServerState}
            chatbotDraftServerState={chatbotDraftServerState}
            handleFileUpload={handleFileUpload}
            setModalData={setModalData}
            isUpdatePending={isUpdatePending}
            isDraftSaved={isDraftSaved}
            handleGenerateChatBot={handleGenerateChatBot}
          />
        </div>
      </div>
      <div className='writer-actions bg-brand-grey-bg border-brand-gray-light sticky bottom-0 z-[30] flex w-full items-center gap-[10px] border-t p-4'>
        <ChatActions
          handleGenerateChatBot={handleGenerateChatBot}
          isEditMode={Boolean(chatbotServerState || chatbotDraftServerState)}
          loadingState={loadingState}
          handleResetToServerState={() =>
            (chatbotServerState || chatbotDraftServerState) &&
            handleSelectBot(chatBotInputValues.selectedChatBot)
          }
          isUpdatePending={isUpdatePending}
          isDraftSaved={isDraftSaved}
          isDraft={Boolean(chatbotDraftServerState)}
          setModalData={setModalData}
          chatBotList={chatBotList}
          chatBotInputValues={chatBotInputValues}
          chatBotSettings={chatBotSettings}
          context={context}
          t={t}
          handleUndoReset={() => {
            dispatch(setSelectedChatBotValues(chatBotInputValues))
            dispatch(setSelectedChatBotSettings(chatBotSettings))
          }}
          handleClearFields={() => dispatch(setClearFields())}
          DuplicateModalView={DuplicateModalView}
        />
      </div>
    </div>
  )

  useImperativeHandle(ref, () => {
    return {
      handleCloseModal() {
        handleCloseModal()
      },
    }
  }, [handleCloseModal])

  if (loadingState.chatbot || (isFetchChatbotlistLoading && !chatBotList.length)) return <Loader />

  const modalContent = {
    'submit-image': (
      <div className='space-y-[5px] p-[24px]'>
        <p className='body-secondary text-brand-violet-light mb-6'>
          {t(
            'eleo-your-story-submit-image-description',
            'Added images can be referenced by Eleo in coversations. Use Your Story to describe when to use each image'
          )}
        </p>

        <Microbutton
          type='large-font'
          text={t('eleo-choose-from-gallery', 'Choose from gallery')}
          onClick={() => {
            setModalData({
              isVisible: true,
              title: (
                <T
                  keyName='eleo-your-story-select-gallery-image-modal-title'
                  defaultValue='Select image'
                />
              ),
              subtitle: (
                <T
                  keyName='eleo-your-story-select-gallery-image-modal-subtitle'
                  defaultValue='Select one of the images from your gallery'
                />
              ),
              type: 'select-from-gallery',
              cancelLabel: t('eleo-go-back', 'Go back'),
            })
          }}
        />
        <div className='body-secondary text-center'>{t('eleo-or', 'or')}</div>
        <label
          className='h-full w-full cursor-pointer items-center justify-center font-medium'
          htmlFor='image-input'
        >
          <Microbutton type='large-font' className='h-[34px] !p-0'>
            {t('eleo-upload', 'Upload')}
          </Microbutton>
        </label>
      </div>
    ),

    'select-from-gallery': (
      <div className='max-h-[80dvh] w-full overflow-y-auto'>
        <div className='px-6'>
          <p className='body-small text-brand-gray-dark mt-2'>
            <T
              keyName='eleo-modal-save-to-gallery-gallery-selection-label'
              defaultValue='Choose gallery'
            />
          </p>
          <ReactSelect
            options={galleries.data}
            setSelectedValue={({ value, label }) => {
              setGalleryItems({ isLoading: false, data: [] })
              setSelectedGallery({ value, label })
            }}
            value={selectedGallery}
            isClearable={false}
          />

          <div className='-mx-6 my-[14px] border-b'></div>
          <Input
            className='h-[40px] w-full self-end'
            placeholder={t('eleo-tool-gallery-search-placeholder', 'Search')}
            onChange={setGallerySearch}
            value={gallerySearch}
          />

          <div className='-mx-6 mt-[14px] border-b'></div>
        </div>
        <div className='relative flex h-[500px] flex-wrap justify-center gap-[20px] gap-y-4 overflow-scroll overflow-x-clip px-6 py-4'>
          {galleryItems.isLoading ? (
            <div className='absolute inset-0 flex items-center justify-center'>
              <CircularProgress className='!text-brand-violet-light' />
            </div>
          ) : (
            <>
              {galleryItems?.data.length ? (
                galleryItems?.data
                  .filter(
                    (image) =>
                      image.name.toLowerCase().includes(gallerySearch.toLowerCase()) ||
                      image.description?.toLowerCase().includes(gallerySearch.toLowerCase())
                  )
                  .map((image) => (
                    <div
                      key={image._id}
                      className={classNames(
                        'group flex cursor-pointer flex-col items-center self-start'
                      )}
                      onClick={() => {
                        if (modalData.context?.onSelect) {
                          modalData.context.onSelect(image.image)
                        } else {
                          const exisitingDoc = documentContext.docContext.find(
                            (i) => i.content === image.image
                          )
                          if (exisitingDoc) documentContext.removeDocument(exisitingDoc.id)
                          else {
                            documentContext.addDocument({
                              label: image.name,
                              type: 'gallery_image',
                              content: image.image,
                              description: image.description,
                              id: image.id ?? v4(),
                            })
                          }
                        }
                        // setModalData(DEFAULT_MODAL_DATA)
                      }}
                    >
                      <div className='list-position mb-1 max-w-[200px] truncate text-center'>
                        {image.name}
                      </div>
                      <div className='relative'>
                        <img
                          className={classNames(
                            'relative max-h-[130px] min-h-[60px] rounded-[4px] shadow-md transition-all group-hover:scale-105',
                            documentContext.docContext.some((i) => i.content === image.image) &&
                              'scale-105'
                          )}
                          src={image.image}
                          alt=''
                          loading='lazy'
                        />
                        {documentContext.docContext.some((i) => i.content === image.image) && (
                          <div className='absolute right-0 top-1 flex size-[14px] rounded-full bg-black text-green-500 shadow-lg'>
                            <CheckCircle fontSize='small' className='-ml-[3px] -mt-[3px]' />
                          </div>
                        )}{' '}
                      </div>
                    </div>
                  ))
              ) : (
                <div className='px-8 py-5 text-xl font-semibold'>
                  {selectedGallery?.value === 'wikimedia-commons' ? (
                    <T keyName='red-parrot-wikimedia-search-modal'>Search for images above</T>
                  ) : (
                    <T keyName='red-parrot-empty-gallery-text-modal'>
                      Nothing here yet. You can upload images to your gallery
                    </T>
                  )}
                </div>
              )}
              <div className='w-[130px]' />
              <div className='w-[130px]' />
            </>
          )}
        </div>
      </div>
    ),
  }

  return (
    <div className={classNames('relative flex h-full flex-col')}>
      {/* Header */}
      <ChatbotGeneratorHeader
        handleHideModal={handleCloseModal}
        chatbotId={chatBotInputValues?.selectedChatBot || chatbotId}
        isDraft={Boolean(chatbotDraftServerState)}
        chatBotList={chatBotList}
        t={t}
      />

      <div className='h-[calc(100%-57px)]'>
        <ToolLayoutPrimary
          breakpoint={520}
          useTabularLayout
          folded={folded}
          setFolded={setFolded}
          storageKey='chatbot'
          maxPanelWidth={800}
          minPanelWidth={350}
          leftSideContent={generatorLayout}
          rightSideContent={previewLayout}
          tabs={{
            form: {
              title: <T keyName='eleo-chatbot-creation-tab-form'>Settings</T>,
              component: generatorLayout,
            },
            preview: {
              title: <T keyName='eleo-chatbot-creation-tab-preview'>Preview</T>,
              component: previewLayout,
            },
            publishing: {
              title: (
                <div className='flex items-center gap-[3px] '>
                  <div className>
                    <T keyName='eleo-chatbot-creation-tab-publishing'>Publishing</T>
                  </div>
                  {Object.keys(publishState).some(
                    (type) =>
                      publishState[type] === 'unpublished' && chatBotSettings.publishing?.[type]
                  ) && <div className='bg-brand-pink size-3 rounded-full [&:nth-child(2)]:ml-1' />}
                  {Object.keys(publishState).some(
                    (type) => publishState[type] === 'pending' && chatBotSettings.publishing?.[type]
                  ) && <div className='size-3 rounded-full bg-[#F38F2B] [&:nth-child(2)]:ml-1' />}
                </div>
              ),
              component: publishingLayout,
            },
          }}
        />
      </div>

      <Modal
        isVisible={modalData.isVisible}
        containerClasses='bg-black bg-opacity-[3%]'
        hideModal={(shouldSave) => {
          setModalData(DEFAULT_MODAL_DATA)

          if (shouldSave && modalData.type !== 'override') handleHideModal()
        }}
        callback={() => {
          if (modalData.type === 'override') {
            handleOverrideBot(chatBotInputValues.selectedChatBot, modalData.context.id)
          }
        }}
        title={modalData.title}
        subtitle={modalData.subtitle}
        acceptLabel={modalData.acceptLabel}
        cancelLabel={modalData.cancelLabel}
        isValid
        isPrimary
        width={500}
        content={modalContent[modalData.type]}
      />
      <HelpTooltips tool='chatBotPanel' />
      {/* <Tour steps={steps} name='chatbot' callback={handleTourProgress} /> */}
      <ContextModal
        documentContext={documentContext}
        docOptions={['documents', 'stories', 'websites', 'databases', 'images']}
        setModalData={setModalData}
      />
    </div>
  )
})

export default ChatBot
